import type {Component, Editor} from 'grapesjs';
import {EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {isMultiselect} from '@EmailMakerReact/lib/email-maker-component-query-library';
import type {EmailMakerImmutableStore} from '@EmailMakerReact/email-maker-react.types';
import type {EmailMakerSectorEntry, EmailMakerSectorMatcher, EmailMakerSectorVisibilityCtx} from './right-panel-registry.types';

/**
 * Resolver walks entries in insertion order and returns the first match, so predicate matchers
 * (IS_MEDIA, fallback) must be registered after all exact-type entries.
 */

interface RegistryEntry {
  matcher: EmailMakerSectorMatcher;
  entry: EmailMakerSectorEntry;
}

const REGISTRY: RegistryEntry[] = [];

export function registerEmailMakerSector(matcher: EmailMakerSectorMatcher, entry: EmailMakerSectorEntry): void {
  REGISTRY.push({matcher, entry});
}

export function registerEmailMakerSectorForTypes(types: readonly string[], entry: EmailMakerSectorEntry): void {
  for (const type of types) {
    registerEmailMakerSector(type, entry);
  }
}

function matchesEntry(entry: RegistryEntry, component: Component, editor: Editor): boolean {
  const {matcher} = entry;
  if (typeof matcher === 'function') {
    return matcher(component, editor);
  }
  return component.get('type') === matcher;
}

/**
 * Returns only the first matching sector, then filters it through its own `isVisible` guard.
 */
export function resolveSectorsForComponent(editor: Editor, component: Component, store: EmailMakerImmutableStore): EmailMakerSectorEntry[] {
  const ctx: EmailMakerSectorVisibilityCtx = {component, editor, store};

  const resolved: EmailMakerSectorEntry[] = [];

  for (const registryEntry of REGISTRY) {
    if (matchesEntry(registryEntry, component, editor)) {
      const {entry} = registryEntry;
      if (!entry.isVisible || entry.isVisible(ctx)) {
        resolved.push(entry);
      }
      break;
    }
  }

  return resolved;
}

export function resolveSectorTitle(entry: EmailMakerSectorEntry, component: Component): string {
  return typeof entry.title === 'function' ? entry.title(component) : entry.title;
}

export function isMultiselectComponent(_component: Component, editor: Editor): boolean {
  return isMultiselect(editor);
}

export function isMediaComponent(component: Component): boolean {
  return Boolean(component.get(EMAIL_MAKER_PROPS.IS_MEDIA));
}

/** True for PMW_TEXT, default 'text', and anchor tags that are NOT PMW_BUTTON. */
export function isTextComponent(component: Component): boolean {
  const type: unknown = component.get('type');
  if (type === EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT) {
    return true;
  }
  if (type === EMAIL_MAKER_COMPONENT_TYPES.default.TEXT) {
    return true;
  }
  const tagName: unknown = component.get('tagName');
  const tagNameStr = typeof tagName === 'string' ? tagName : undefined;
  return tagNameStr?.toLowerCase() === 'a' && type !== EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON;
}

export function isSectionOrTableComponent(component: Component): boolean {
  const type: unknown = component.get('type');
  return type === EMAIL_MAKER_COMPONENT_TYPES.default.TABLE || type === EMAIL_MAKER_COMPONENT_TYPES.table.PMW_SECTION;
}
